import {
  createAnswer,
  deleteAnswers,
  getAllAnswers,
  getAnswerById,
  markAnswersAsSent,
  updateAnswer,
} from '@services/answerService';
import { answerActionTypes } from './actionTypes/answer';
import { failure, loading, totalItems } from './commonActions';
import { Dispatch } from 'redux';
import { setNotification } from './appActions';
import {
  AnswerEntityInterface,
  AnswerQueryInterface,
  CreateAnswerRequestInterface,
  DeleteAnswersRequestInterface,
  PatchAnswersAsSentRequestInterface,
  UpdateAnswerRequestInterface,
} from '@types';
import {
  setAnswerFiltersPartially,
  setIsOpenAnswersDeleteModal,
  setIsOpenMarkAsSentModal,
} from '@actions/answersPageAction';
import {
  setAnswerContext,
  setAnswerQuestionnaireContext,
  setAnswerUser,
  setAnswerUserAbbr,
} from '@actions/answerEnvironmentActions';
import { getUserFullNameInitials } from '@utils';

export const setAnswers = (answers: AnswerEntityInterface[] = []) => {
  return {
    type: answerActionTypes.SET_ANSWERS,
    payload: [...answers],
  };
};

export const setCurrentAnswer = (answer: AnswerEntityInterface) => {
  return {
    type: answerActionTypes.SET_CURRENT_ANSWER,
    payload: answer,
  };
};

export const setCurrentAnswerData = (answerData: string) => {
  return {
    type: answerActionTypes.SET_CURRENT_ANSWER_DATA,
    payload: answerData,
  };
};

export const setEditingAnswer = (value: boolean) => {
  return {
    type: answerActionTypes.SET_EDITING_ANSWER,
    payload: value,
  };
};

export const setViewMode = (mode: boolean) => {
  return {
    type: answerActionTypes.SET_EDIT_MODE,
    payload: mode,
  };
};

export const setIsDateFromSaved = (isSaved: boolean) => {
  return {
    type: answerActionTypes.SET_IS_DATE_FROM_SAVED,
    payload: isSaved,
  };
};

export const setIsDateToSaved = (isSaved: boolean) => {
  return {
    type: answerActionTypes.SET_IS_DATE_TO_SAVED,
    payload: isSaved,
  };
};

export const setNavigateToId = (id: string) => {
  return {
    type: answerActionTypes.SET_NAVIGATE_TO_ID,
    payload: id,
  };
};

export const setCheckedAnswers = (
  answersIds: string[]
): { type: answerActionTypes; payload: string[] } => {
  return {
    type: answerActionTypes.SET_CHECKED_ANSWERS,
    payload: answersIds,
  };
};

export const makeCurrentAnswer = (id: string) => {
  const instanceName = 'current_answer';

  return async (dispatch: Dispatch) => {
    dispatch(setCurrentAnswer(null));
    try {
      dispatch(loading(true, instanceName));
      const answer = await getAnswerById(id);
      const questionnaireData = JSON.parse(answer.questionnaire.data);
      dispatch(setAnswerUser(answer.owner));
      dispatch(setAnswerUserAbbr(getUserFullNameInitials(answer.owner)));
      dispatch(setAnswerQuestionnaireContext(questionnaireData));
      dispatch(setCurrentAnswerData(answer.data));
      dispatch(setAnswerUser(answer.owner));
      dispatch(setAnswerContext(answer.data));
      dispatch(setCurrentAnswer(answer));
    } catch (err) {
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeAllAnswers = (query: AnswerQueryInterface) => {
  const instanceName = 'answers';
  return async (dispatch) => {
    try {
      dispatch(failure(false, instanceName));
      dispatch(loading(true, 'bar'));
      dispatch(loading(true, instanceName));

      const { items, total } = await getAllAnswers(query);
      dispatch(setAnswers(items));
      dispatch(totalItems(total, instanceName));
    } catch (err) {
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, 'bar'));
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeCreateAnswer = (data: CreateAnswerRequestInterface) => {
  const instanceName = 'create_answer';
  return async (dispatch: Dispatch) => {
    try {
      dispatch(loading(true, instanceName));
      const { id } = await createAnswer(data);
      dispatch(setNotification({ textKey: 'answer_success_sent', variant: 'success' }));
      dispatch(setNavigateToId(id));
    } catch (err) {
      dispatch(setNotification({ textKey: 'answer_error_sent', variant: 'error' }));
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeUpdateAnswer = (id: string, data: UpdateAnswerRequestInterface) => {
  const instanceName = 'editing_answer';
  return async (dispatch) => {
    try {
      dispatch(loading(true, instanceName));
      await updateAnswer(id, data);
      dispatch(setNavigateToId(id));
    } catch (err) {
      dispatch(failure(true, instanceName));
    } finally {
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeDeleteAnswers = (data: DeleteAnswersRequestInterface) => {
  const instanceName = 'deleting_answers';

  return async (dispatch, getState) => {
    try {
      const checkedAnswersIds = getState()?.answer.checkedAnswersIds;

      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));
      await deleteAnswers(data);

      dispatch(setCheckedAnswers(checkedAnswersIds.filter((id) => !data.answersIds.includes(id))));
      dispatch(setAnswerFiltersPartially({}, true));

      dispatch(
        setNotification({
          textKey:
            data.answersIds.length > 1 ? 'answers_success_deleted' : 'answer_success_deleted',
          variant: 'success',
        })
      );
    } catch (err) {
      dispatch(failure(true, instanceName));
      dispatch(
        setNotification({
          textKey: data.answersIds.length > 1 ? 'answers_error_deleted' : 'answer_error_deleted',
          variant: 'error',
        })
      );
    } finally {
      dispatch(setIsOpenAnswersDeleteModal(false));
      dispatch(loading(false, instanceName));
    }
  };
};

export const makeMarkAnswersAsSent = (data: PatchAnswersAsSentRequestInterface) => {
  const instanceName = 'marking_as_sent';
  const { answersIds } = data;
  return async (dispatch) => {
    try {
      dispatch(failure(false, instanceName));
      dispatch(loading(true, instanceName));
      await markAnswersAsSent(data);
      dispatch(setAnswerFiltersPartially());

      if (answersIds.length > 1) {
        dispatch(setNotification({ textKey: 'answers_mark_sent', variant: 'success' }));
      } else {
        dispatch(setNotification({ textKey: 'answer_mark_sent', variant: 'success' }));
      }
    } catch (err) {
      dispatch(failure(true, instanceName));
      if (answersIds.length > 1) {
        dispatch(setNotification({ textKey: 'answers_error_mark_sent', variant: 'error' }));
      } else {
        setNotification({ textKey: 'answer_error_mark_sent', variant: 'error' });
      }
    } finally {
      dispatch(loading(false, instanceName));
      dispatch(setIsOpenMarkAsSentModal(false));
    }
  };
};
